<template>
  <div class="v2-ship-step">
    <h3 class="v2-ship-step__title">
      {{ isCompleted ? 'Shipment Summary' : 'Creating Shipments…' }}
    </h3>
    <p
      v-if="displayTriggeredBy"
      class="v2-ship-step__subtitle"
    >
      Triggered by <strong>{{ displayTriggeredBy }}</strong>
    </p>

    <div class="v2-ship-progress">
      <div class="v2-ship-progress__bar">
        <div
          class="v2-ship-progress__fill"
          :class="`v2-ship-progress__fill--${progressVariant}`"
          :style="{ width: `${progressPct}%` }"
        />
      </div>
      <div class="v2-ship-progress__meta">
        <span>{{ processed }} / {{ totalOrders }} processed</span>
        <span
          v-if="!isCompleted"
          class="v2-ship-progress__inflight"
        >
          <v2-spinner size="sm" />
          Processing…
        </span>
      </div>
    </div>

    <div class="v2-ship-stats">
      <div class="v2-ship-stats__cell v2-ship-stats__cell--success">
        <div class="v2-ship-stats__value">
          {{ succeeded }}
        </div>
        <div class="v2-ship-stats__label">
          Succeeded
        </div>
      </div>
      <div class="v2-ship-stats__cell v2-ship-stats__cell--danger">
        <div class="v2-ship-stats__value">
          {{ failed }}
        </div>
        <div class="v2-ship-stats__label">
          Failed
        </div>
      </div>
      <div class="v2-ship-stats__cell v2-ship-stats__cell--warning">
        <div class="v2-ship-stats__value">
          {{ skipped }}
        </div>
        <div class="v2-ship-stats__label">
          Skipped
        </div>
      </div>
    </div>

    <template v-if="isCompleted && failedResults.length">
      <div class="v2-ship-step__section-header">
        <h4 class="v2-ship-step__section-title">
          Failed Shipments
        </h4>
        <v2-button
          variant="outline"
          size="sm"
          :disabled="isRetrying"
          @click="retryAllFailed"
        >
          <v2-spinner
            v-if="isRetrying"
            size="sm"
          />
          Retry All Failed
        </v2-button>
      </div>

      <v2-table
        :columns="failedColumns"
        :items="failedResults"
      >
        <template #cell(reason)="{ item }">
          <span class="v2-ship-step__reason v2-ship-step__reason--danger">
            {{ item.reason }}
          </span>
        </template>
        <template #cell(actions)="{ item }">
          <v2-button
            variant="ghost"
            size="sm"
            :disabled="isRetrying"
            @click="retrySingle(item.orderId)"
          >
            Retry
          </v2-button>
        </template>
      </v2-table>
    </template>

    <template v-if="isCompleted && skippedResults.length">
      <div class="v2-ship-step__section-header">
        <h4 class="v2-ship-step__section-title">
          Skipped Orders
        </h4>
      </div>
      <v2-table
        :columns="skippedColumns"
        :items="skippedResults"
      >
        <template #cell(reason)="{ item }">
          <span class="v2-ship-step__reason v2-ship-step__reason--warning">
            {{ item.reason }}
          </span>
        </template>
      </v2-table>
    </template>
  </div>
</template>

<script>
import store from '@/store'
import { GET_BULK_SHIPMENT_STATUS, RETRY_BULK_SHIPMENT } from '@/store/modules/shipment.module'
import { apiToastError } from '@/@core/utils/toast'
import V2Button from '@/components/v2/V2Button.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Table from '@/components/v2/V2Table.vue'

export default {
  name: 'V2ShipAllProcessingStep',
  components: { V2Button, V2Spinner, V2Table },
  props: {
    batchId: { type: String, required: true },
    batchStatus: { type: String, default: 'processing' },
    triggeredBy: { type: String, default: '' },
  },
  data() {
    return {
      pollingTimeoutId: null,
      pollRound: 0,
      totalOrders: 0,
      processed: 0,
      succeeded: 0,
      failed: 0,
      skipped: 0,
      results: [],
      isRetrying: false,
      localTriggeredBy: '',
      failedColumns: [
        { key: 'orderSeqId', label: 'Order ID' },
        { key: 'retailerName', label: 'Retailer' },
        { key: 'reason', label: 'Reason' },
        { key: 'actions', label: '' },
      ],
      skippedColumns: [
        { key: 'orderSeqId', label: 'Order ID' },
        { key: 'retailerName', label: 'Retailer' },
        { key: 'reason', label: 'Reason' },
      ],
    }
  },
  computed: {
    displayTriggeredBy() {
      return this.localTriggeredBy || this.triggeredBy
    },
    isCompleted() {
      return this.batchStatus === 'completed' || this.batchStatus === 'failed'
    },
    progressPct() {
      if (!this.totalOrders) return 0
      return Math.min(100, Math.round((this.processed / this.totalOrders) * 100))
    },
    progressVariant() {
      if (this.isCompleted && this.failed > 0) return 'warning'
      if (this.isCompleted) return 'success'
      return 'primary'
    },
    failedResults() {
      return this.results.filter(r => r.status === 'failed')
    },
    skippedResults() {
      return this.results.filter(r => r.status === 'skipped')
    },
  },
  mounted() {
    this.startPolling()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    startPolling(options = {}) {
      const { delayFirstPoll = false } = options
      this.stopPolling()
      const scheduleNext = () => {
        if (this.isCompleted || this._isDestroyed) return
        this.pollingTimeoutId = setTimeout(() => {
          this.runPollThenSchedule(scheduleNext)
        }, 2000)
      }
      if (delayFirstPoll) {
        this.pollingTimeoutId = setTimeout(() => {
          this.runPollThenSchedule(scheduleNext)
        }, 2000)
      } else {
        this.runPollThenSchedule(scheduleNext)
      }
    },
    stopPolling() {
      if (this.pollingTimeoutId) {
        clearTimeout(this.pollingTimeoutId)
        this.pollingTimeoutId = null
      }
    },
    async runPollThenSchedule(scheduleNext) {
      let done = false
      try {
        done = await this.pollStatus()
      } finally {
        if (!done) scheduleNext()
      }
    },
    async pollStatus() {
      this.pollRound += 1
      const round = this.pollRound
      try {
        const res = await store.dispatch(GET_BULK_SHIPMENT_STATUS, this.batchId)
        if (round !== this.pollRound) return false
        const data = res?.data?.data
        if (!data) return false
        this.applyStatusData(data)
        return data.status === 'completed' || data.status === 'failed'
      } catch {
        return false
      }
    },
    applyStatusData(data) {
      this.totalOrders = data.totalOrders
      this.processed = data.processed
      this.succeeded = data.succeeded
      this.failed = data.failed
      this.skipped = data.skipped
      this.results = data.results || []
      if (data.triggeredBy) this.localTriggeredBy = data.triggeredBy
      this.$emit('status-update', {
        status: data.status,
        totalOrders: data.totalOrders,
        succeeded: data.succeeded,
        failed: data.failed,
        skipped: data.skipped,
        triggeredBy: data.triggeredBy || '',
      })
    },
    async performRetry(payload = {}) {
      this.isRetrying = true
      try {
        await store.dispatch(RETRY_BULK_SHIPMENT, {
          batchId: this.batchId,
          payload,
        })
        this.$emit('retry')
        this.startPolling({ delayFirstPoll: true })
      } catch (err) {
        apiToastError(err)
      } finally {
        this.isRetrying = false
      }
    },
    retryAllFailed() {
      return this.performRetry({})
    },
    retrySingle(orderId) {
      return this.performRetry({ orderIds: [orderId] })
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-ship-step {
  padding: var(--v2-space-2) var(--v2-space-1);

  &__title {
    font-family: var(--v2-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0 0 var(--v2-space-2) 0;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    margin: 0 0 var(--v2-space-4) 0;
  }

  &__section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: var(--v2-space-5) 0 var(--v2-space-2);
  }

  &__section-title {
    font-family: var(--v2-font-display);
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0;
  }

  &__reason {
    font-size: 0.75rem;

    &--danger { color: var(--v2-destructive); }
    &--warning { color: var(--v2-warning, #f59e0b); }
  }
}

.v2-ship-progress {
  margin-bottom: var(--v2-space-5);

  &__bar {
    width: 100%;
    height: 8px;
    background: var(--v2-glass-surface-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: 999px;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    transition: width 0.3s ease;
    border-radius: 999px;

    &--primary { background: var(--v2-primary); }
    &--success { background: oklch(0.7 0.18 145); }
    &--warning { background: var(--v2-warning, #f59e0b); }
  }

  &__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin-top: var(--v2-space-2);
  }

  &__inflight {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-1);
  }
}

.v2-ship-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--v2-space-3);
  margin-bottom: var(--v2-space-5);

  &__cell {
    text-align: center;
    padding: var(--v2-space-3);
    background: var(--v2-glass-surface-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
  }

  &__value {
    font-family: var(--v2-font-display);
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
  }

  &__label {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin-top: var(--v2-space-1);
  }

  &__cell--success &__value { color: oklch(0.62 0.18 145); }
  &__cell--danger &__value { color: var(--v2-destructive); }
  &__cell--warning &__value { color: var(--v2-warning, #f59e0b); }
}
</style>
